import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ============================================================
// Réseaux Sociaux — gestion des comptes du marchand et lecture des
// analyses enregistrées (`social_accounts`, `social_analyses`).
// Isolé par commerce.
// ============================================================

interface SocialAccount {
  id: number;
  platform: string;
  profile_url: string;
  display_name: string | null;
}

interface Analysis {
  platform: string;
  followers: number | null;
  following: number | null;
  posts: number | null;
  engagement_rate: number | null;
  analyzed_at: string;
}

const PLATFORMS = [
  { key: 'facebook', label: 'Facebook', icon: 'ri-facebook-fill', color: 'text-[#1877F2]' },
  { key: 'instagram', label: 'Instagram', icon: 'ri-instagram-line', color: 'text-[#E4405F]' },
  { key: 'tiktok', label: 'TikTok', icon: 'ri-tiktok-fill', color: 'text-foreground-900' },
  { key: 'youtube', label: 'YouTube', icon: 'ri-youtube-fill', color: 'text-[#FF0000]' },
  { key: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-fill', color: 'text-[#0A66C2]' },
  { key: 'x', label: 'X (Twitter)', icon: 'ri-twitter-x-fill', color: 'text-foreground-900' },
];

export default function SocialNetworksPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [platform, setPlatform] = useState('instagram');
  const [profileUrl, setProfileUrl] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [formMsg, setFormMsg] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const cid = getCommerceId(user);
      const [accRes, anaRes] = await Promise.all([
        supabase.from('social_accounts').select('id, platform, profile_url, display_name').eq('user_id', cid).order('created_at', { ascending: false }),
        supabase.from('social_analyses').select('platform, followers, following, posts, engagement_rate, analyzed_at').eq('user_id', cid).order('analyzed_at', { ascending: false }),
      ]);
      if (accRes.error) throw accRes.error;
      setAccounts((accRes.data || []) as SocialAccount[]);
      setAnalyses((anaRes.data || []) as Analysis[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async () => {
    if (!user) return;
    if (!profileUrl.trim()) { setFormMsg('Le lien du profil est requis.'); return; }
    setSaving(true);
    setFormMsg(null);
    try {
      const key = profileUrl.trim().replace(/\/+$/, '').split('/').pop() || '';
      const { error: err } = await supabase.from('social_accounts').insert({
        user_id: getCommerceId(user),
        platform,
        profile_url: profileUrl.trim(),
        profile_key: key,
        display_name: displayName.trim() || null,
      });
      if (err) throw err;
      setModalOpen(false);
      setProfileUrl(''); setDisplayName(''); setPlatform('instagram');
      fetchData();
    } catch (e: unknown) {
      setFormMsg(e instanceof Error ? e.message : 'Erreur lors de l’ajout');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (acc: SocialAccount) => {
    await supabase.from('social_accounts').delete().eq('id', acc.id);
    await supabase.from('social_analyses').delete().eq('user_id', getCommerceId(user)).eq('platform', acc.platform);
    setDeleteId(null);
    fetchData();
  };

  const platformMeta = (key: string) => PLATFORMS.find((p) => p.key === key) || { label: key, icon: 'ri-links-line', color: 'text-foreground-600' };
  const latestFor = (platformKey: string) => analyses.find((a) => a.platform === platformKey);

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950"><i className="ri-share-circle-line mr-2 text-accent-600"></i>Réseaux Sociaux</h2>
          <p className="text-sm text-foreground-500 mt-1">Centralisez vos comptes et suivez leurs statistiques</p>
        </div>
        <button onClick={() => { setModalOpen(true); setFormMsg(null); }} className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Ajouter un compte
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : error ? (
        <div className="flex flex-col items-center py-16"><p className="text-sm text-red-500 mb-3">{error}</p><button onClick={fetchData} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm cursor-pointer">Réessayer</button></div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <i className="ri-share-line text-4xl text-foreground-300 mb-3 block"></i>
          <h3 className="text-base font-semibold text-foreground-700 mb-1">Aucun compte connecté</h3>
          <p className="text-sm text-foreground-500 mb-4">Ajoutez vos réseaux sociaux pour les centraliser</p>
          <button onClick={() => setModalOpen(true)} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600">Ajouter un compte</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map((acc) => {
            const meta = platformMeta(acc.platform);
            const ana = latestFor(acc.platform);
            return (
              <div key={acc.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <i className={`${meta.icon} text-xl ${meta.color}`}></i>
                    <span className="text-sm font-semibold text-foreground-900">{meta.label}</span>
                  </div>
                  <button onClick={() => setDeleteId(acc.id)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                </div>
                <p className="text-xs text-foreground-500 truncate mb-3">{acc.display_name || acc.profile_url}</p>
                {ana ? (
                  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-background-200/70">
                    <div className="text-center"><div className="text-sm font-bold text-foreground-900">{(ana.followers ?? 0).toLocaleString()}</div><div className="text-[10px] text-foreground-400">Abonnés</div></div>
                    <div className="text-center"><div className="text-sm font-bold text-foreground-900">{(ana.posts ?? 0).toLocaleString()}</div><div className="text-[10px] text-foreground-400">Posts</div></div>
                    <div className="text-center"><div className="text-sm font-bold text-accent-600">{ana.engagement_rate != null ? `${ana.engagement_rate}%` : '—'}</div><div className="text-[10px] text-foreground-400">Engagement</div></div>
                  </div>
                ) : (
                  <p className="text-xs text-foreground-400 pt-3 border-t border-background-200/70">Aucune analyse enregistrée</p>
                )}
                <a href={acc.profile_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-primary-600 hover:underline">
                  <i className="ri-external-link-line"></i> Ouvrir le profil
                </a>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setModalOpen(false)}></div>
          <div className="relative bg-background-50 rounded-xl w-full max-w-md p-6 animate-scale-in">
            <h3 className="text-lg font-bold font-heading text-foreground-950 mb-4">Ajouter un compte</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Plateforme</label>
                <div className="grid grid-cols-3 gap-2">
                  {PLATFORMS.map((p) => (
                    <button key={p.key} onClick={() => setPlatform(p.key)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors cursor-pointer ${platform === p.key ? 'border-primary-400 bg-primary-50/50' : 'border-background-200/70 hover:border-background-300/60'}`}>
                      <i className={`${p.icon} text-lg ${p.color}`}></i>
                      <span className="text-[11px] text-foreground-600">{p.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Lien du profil</label>
                <input type="url" value={profileUrl} onChange={(e) => setProfileUrl(e.target.value)} placeholder="https://instagram.com/votre-page"
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nom affiché (optionnel)</label>
                <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300" />
              </div>
              {formMsg && <div className="px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs">{formMsg}</div>}
            </div>
            <div className="flex items-center justify-end gap-3 mt-5">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-background-100 text-foreground-600 rounded-full text-sm font-medium cursor-pointer">Annuler</button>
              <button onClick={handleAdd} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 disabled:opacity-50">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteId(null)}></div>
          <div className="relative bg-background-50 rounded-xl p-6 w-full max-w-sm animate-scale-in">
            <h3 className="text-lg font-bold font-heading text-foreground-950 mb-2">Supprimer ce compte ?</h3>
            <p className="text-sm text-foreground-500 mb-5">Les analyses associées seront également supprimées.</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium cursor-pointer">Annuler</button>
              <button onClick={() => { const acc = accounts.find((a) => a.id === deleteId); if (acc) handleDelete(acc); }} className="px-4 py-2 bg-red-500 text-white rounded-full text-sm font-medium cursor-pointer hover:bg-red-600">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}